<template>
    <div v-if="loaded" class="nevs-content">
        <div class="nevs-content-top-buttons">
            <NevsButton v-show="currentTab === 1" class="success" @click="saveClick()"><i class="fa-solid fa-floppy-disk"></i>
                {{ $LANG.Get('buttons.save') }}
            </NevsButton>
            <span v-show="currentTab === 1">&nbsp;</span>
            <template v-if="mode === 'add' && canSeeInvoices">
                <NevsButton v-show="currentTab === 1" class="success" @click="saveClick(true)"><i class="fa-solid fa-file-invoice"></i>
                    {{ $LANG.Get('buttons.saveAndCreateInvoice') }}
                </NevsButton>
                <span v-show="currentTab === 1">&nbsp;</span>
            </template>
            <NevsButton class="error" @click="backClick"><i class="fa-solid fa-arrow-left"></i> {{
                    $LANG.Get('buttons.back')
                }}
            </NevsButton>
            <span v-if="mode === 'edit' && currentTab !== 1 && hasUnsavedChanges" class="unsaved-changes">
                {{ $LANG.Get('labels.unsavedChanges') }} {{ $LANG.Get('buttons.generalData') }}
            </span>
        </div>
        <NevsTabs class="person-tabs" :tabs="tabList" v-model="currentTab"></NevsTabs>
        <NevsTab :value="1" v-model="currentTab">
            <NevsCard class="person-form">
                <div class="person-form-header">
                    <div class="person-form-header-photo">
                        <NevsPhotoUpload :accept="'image/*'" v-model="person.photo"></NevsPhotoUpload>
                    </div>
                    <div class="person-form-header-fields">
                        <NevsTextField v-model="person.first_name" :error="personValidation.first_name"
                                       :label="'*' + $LANG.Get('fields.firstName') + ':'"></NevsTextField>
                        <NevsTextField v-model="person.last_name" :error="personValidation.last_name"
                                       :label="'*' + $LANG.Get('fields.lastName') + ':'"></NevsTextField>
                    </div>
                </div>

                <div class="person-form-grid">
                    <NevsTextField v-model="person.oib" :error="personValidation.oib"
                                   :label="$LANG.Get('fields.oib') + ':'"></NevsTextField>
                    <NevsSelect :nullable="true" v-model="person.gender_id" :ajax="'select/genders'"
                                :label="$LANG.Get('fields.gender') + ':'"></NevsSelect>
                    <NevsDateField v-model="person.date_of_birth"
                                   :label="$LANG.Get('fields.dateOfBirth') + ':'"></NevsDateField>
                    <NevsSelect :nullable="true" v-model="person.company_id" :ajax="'select/companies'"
                                :label="$LANG.Get('fields.company') + ':'"></NevsSelect>
                    <NevsTextField v-model="person.member_id"
                                   :label="$LANG.Get('fields.memberId') + ':'"></NevsTextField>
                    <NevsSelect :nullable="true" v-model="person.user_id" :ajax="'select/users'"
                                :label="$LANG.Get('fields.trainer') + ':'"></NevsSelect>
                </div>

                <div class="person-form-foreign">
                    <NevsTextField v-if="person.foreign" :width="'300px'" v-model="person.oib_label"
                                   :error="personValidation.oib_label"
                                   :label="$LANG.Get('fields.oibLabel') + ':'"></NevsTextField>
                    <NevsCheckbox v-model="person.foreign" :label="$LANG.Get('fields.foreignClient')"></NevsCheckbox>
                </div>

                <div class="person-form-flags">
                    <NevsCheckbox v-model="person.allows_communication" :label="$LANG.Get('fields.allowsCommunication')"></NevsCheckbox>
                    <NevsCheckbox v-model="person.accepts_gdpr" :label="$LANG.Get('fields.acceptsGdpr')"></NevsCheckbox>
                    <NevsCheckbox v-model="person.active" :label="$LANG.Get('fields.activeF')"></NevsCheckbox>
                </div>

                <div class="person-form-note">
                    <NevsTextArea v-model="person.note" :label="$LANG.Get('fields.note') + ':'"></NevsTextArea>
                </div>

                <div class="person-form-sections">
                    <div class="person-form-section">
                        <h2>{{ $LANG.Get('labels.emails') }}</h2>
                        <div v-if="personValidation.emails" class="box-error nevs-text-error">{{ personValidation.emails }}</div>
                        <div v-for="(email, key) in person.emails" :key="key" class="box-form">
                            <NevsTextField v-model="email.address" :error="email.validation"
                                           :label="'*' + $LANG.Get('fields.address') + ':'"></NevsTextField>
                            <NevsCheckbox v-model="email.official" :label="$LANG.Get('fields.officialF')"></NevsCheckbox>
                            <span :title="$LANG.Get('tooltips.removeItem')" class="box-form-remove-button"
                                  @click="removeRecord(key, 'emails')"><i class="fa-solid fa-trash"></i></span>
                        </div>
                        <NevsButton @click="addRecord('emails')"><i class="fa-solid fa-plus"></i> {{ $LANG.Get('buttons.add') }}</NevsButton>
                    </div>
                    <div class="person-form-section">
                        <h2>{{ $LANG.Get('labels.phones') }}</h2>
                        <div v-if="personValidation.phones" class="box-error nevs-text-error">{{ personValidation.phones }}</div>
                        <div v-for="(phone, key) in person.phones" :key="key" class="box-form">
                            <NevsTextField v-model="phone.name" :error="phone.validation.name"
                                           :label="'*' + $LANG.Get('fields.name') + ':'"></NevsTextField>
                            <NevsTextField v-model="phone.number" :error="phone.validation.number"
                                           :label="'*' + $LANG.Get('fields.number') + ':'"></NevsTextField>
                            <br/><br/><br/>
                            <span :title="$LANG.Get('tooltips.removeItem')" class="box-form-remove-button"
                                  @click="removeRecord(key, 'phones')"><i class="fa-solid fa-trash"></i></span>
                        </div>
                        <NevsButton @click="addRecord('phones')"><i class="fa-solid fa-plus"></i> {{ $LANG.Get('buttons.add') }}</NevsButton>
                    </div>
                    <div class="person-form-section">
                        <h2>{{ $LANG.Get('labels.addresses') }}</h2>
                        <div v-if="personValidation.addresses" class="box-error nevs-text-error">{{
                                personValidation.addresses
                            }}
                        </div>
                        <div v-for="(address, key) in person.addresses" :key="key" class="box-form">
                            <NevsTextArea v-model="address.address" :error="address.validation.address"
                                          :label="$LANG.Get('fields.streetAndHouseNo')"></NevsTextArea>
                            <NevsTextField v-model="address.postal_code" :error="address.validation.postal_code"
                                           :label="$LANG.Get('fields.postalCode')"></NevsTextField>
                            <NevsTextField v-model="address.city" :error="address.validation.city"
                                           :label="$LANG.Get('fields.city')"></NevsTextField>
                            <NevsTextField v-model="address.country" :error="address.validation.country"
                                           :label="$LANG.Get('fields.country')"></NevsTextField>
                            <NevsCheckbox v-model="address.official" :label="$LANG.Get('fields.officialF')"></NevsCheckbox>
                            <span :title="$LANG.Get('tooltips.removeItem')" class="box-form-remove-button"
                                  @click="removeRecord(key, 'addresses')"><i class="fa-solid fa-trash"></i></span>
                        </div>
                        <NevsButton @click="addRecord('addresses')"><i class="fa-solid fa-plus"></i> {{ $LANG.Get('buttons.add') }}</NevsButton>
                    </div>
                    <div v-if="mode === 'edit'" class="person-form-section">
                        <h2>{{ $LANG.Get('labels.membersApp') }}</h2>
                        <p :class="{'members-app-status': true, 'initialized': memberInitialized}">
                            <i :class="memberInitialized ? 'fa-solid fa-circle-check' : 'fa-solid fa-circle-xmark'"></i>
                            {{ memberInitialized ? $LANG.Get('labels.membersAppInitialized') : $LANG.Get('labels.membersAppNotInitialized') }}
                        </p>
                        <NevsButton v-if="!memberInitialized" @click="initializeMember">
                            <i class="fa-solid fa-mobile-screen-button"></i> {{ $LANG.Get('buttons.initializeMemberApp') }}
                        </NevsButton>
                        <NevsButton v-else @click="resetMemberPassword">
                            <i class="fa-solid fa-key"></i> {{ $LANG.Get('buttons.resetMemberPassword') }}
                        </NevsButton>
                    </div>
                </div>
            </NevsCard>
        </NevsTab>
        <NevsTab :value="2" v-model="currentTab">
            <NevsCard>
                <NevsButton class="primary" @click="addPersonMembership"><i class="fa-solid fa-plus"></i> {{ $LANG.Get('buttons.addMembership') }}</NevsButton>
                <div class="person-memberships-grid">
                    <NevsSimpleGrid :fields="personMembershipFields" :records="personMemberships"
                                    :empty-text="$LANG.Get('labels.noMemberships')" :clickable="false"
                                    :row-class="personMembershipRowClass">
                        <template #actions="{ record }">
                            <span v-if="record.products.length > 0" :title="$LANG.Get('labels.linkedProducts')"
                                  class="person-memberships-grid-button person-memberships-grid-button-products"
                                  @click="showMembershipProducts(record)"><i class="fa-solid fa-box-open"></i></span>
                            <span :title="$LANG.Get('tooltips.edit')" class="person-memberships-grid-button"
                                  @click="editPersonMembership(record)"><i class="fa-solid fa-pencil"></i></span>
                            <span v-if="!record.invoice_id" :title="$LANG.Get('tooltips.delete')"
                                  class="person-memberships-grid-button person-memberships-grid-button-delete"
                                  @click="deletePersonMembership(record)"><i class="fa-solid fa-trash"></i></span>
                        </template>
                    </NevsSimpleGrid>
                </div>
                <h3 class="person-pauses-heading">{{ $LANG.Get('labels.pausesHeading') }}</h3>
                <div class="person-pauses-disclaimer">
                    <i class="fa-solid fa-circle-info"></i> {{ $LANG.Get('texts.pauseDisclaimer') }}
                </div>
                <NevsButton class="primary" @click="addPersonPause"><i class="fa-solid fa-plus"></i> {{ $LANG.Get('buttons.addPause') }}</NevsButton>
                <div class="person-pauses-grid">
                    <NevsSimpleGrid :fields="personPauseFields" :records="personPauses"
                                    :empty-text="$LANG.Get('labels.noPauses')" :clickable="false"
                                    :row-class="personPauseRowClass">
                        <template #actions="{ record }">
                            <span :title="$LANG.Get('tooltips.edit')" class="person-pauses-grid-button"
                                  @click="editPersonPause(record)"><i class="fa-solid fa-pencil"></i></span>
                            <span :title="$LANG.Get('tooltips.delete')"
                                  class="person-pauses-grid-button person-pauses-grid-button-delete"
                                  @click="deletePersonPause(record)"><i class="fa-solid fa-trash"></i></span>
                        </template>
                    </NevsSimpleGrid>
                </div>
            </NevsCard>
        </NevsTab>
        <NevsTab :value="3" v-model="currentTab">
            <NevsCard>
                <h3 class="person-products-heading">{{ $LANG.Get('labels.personProductsHeading') }}</h3>
                <NevsButton class="primary" @click="addPersonProduct"><i class="fa-solid fa-plus"></i> {{ $LANG.Get('buttons.addProduct') }}</NevsButton>
                <div class="person-products-grid">
                    <NevsSimpleGrid :fields="personProductFields" :records="personProducts"
                                    :empty-text="$LANG.Get('labels.noProducts')" :clickable="false">
                        <template #actions="{ record }">
                            <span :title="$LANG.Get('tooltips.edit')" class="person-products-grid-button"
                                  @click="editPersonProduct(record)"><i class="fa-solid fa-pencil"></i></span>
                            <span :title="$LANG.Get('tooltips.delete')"
                                  class="person-products-grid-button person-products-grid-button-delete"
                                  @click="deletePersonProduct(record)"><i class="fa-solid fa-trash"></i></span>
                        </template>
                    </NevsSimpleGrid>
                </div>
            </NevsCard>
            <NevsCard class="person-membership-products-card">
                <h3 class="person-products-heading">{{ $LANG.Get('labels.membershipProductsHeading') }}</h3>
                <div v-if="membershipHasActive" class="person-products-grid">
                    <NevsSimpleGrid :fields="personProductFields" :records="membershipProducts"
                                    :empty-text="$LANG.Get('labels.noProducts')" :clickable="false">
                    </NevsSimpleGrid>
                </div>
                <div v-else class="person-membership-products-none">{{ $LANG.Get('labels.noActiveMembership') }}</div>
            </NevsCard>
        </NevsTab>
        <NevsTab :value="4" v-model="currentTab">
            <NevsCard>
                <NevsButton class="primary" @click="addVisit"><i class="fa-solid fa-plus"></i> {{ $LANG.Get('buttons.addVisit') }}</NevsButton>
                <div class="visits-grid">
                    <NevsSimpleGrid :fields="visitFields" :records="visits" :empty-text="$LANG.Get('labels.noVisits')"
                                    :clickable="false">
                        <template #cell-products="{ record }">
                            <span v-if="record.products.length === 0">-</span>
                            <div v-for="(product, key) in record.products" :key="key" class="visit-product">
                                {{ product.product_name }} <span class="visit-product-quantity">({{ product.quantity_display }})</span>
                            </div>
                        </template>
                        <template #actions="{ record }">
                            <span :title="$LANG.Get('tooltips.edit')" class="visits-grid-button"
                                  @click="editVisit(record)"><i class="fa-solid fa-pencil"></i></span>
                            <span :title="$LANG.Get('tooltips.delete')" class="visits-grid-button visits-grid-button-delete"
                                  @click="deleteVisit(record)"><i class="fa-solid fa-trash"></i></span>
                        </template>
                    </NevsSimpleGrid>
                </div>
                <div v-if="visits.length < visitsTotal" class="visits-load-more">
                    <NevsButton @click="loadMoreVisits"><i class="fa-solid fa-chevron-down"></i> {{ $LANG.Get('buttons.loadMore') }}</NevsButton>
                </div>
            </NevsCard>
        </NevsTab>
        <NevsTab :value="5" v-model="currentTab">
            <NevsCard class="documents-card">
                <NevsMultiUpload @uploaded="documentsUploaded"></NevsMultiUpload>
                <div class="documents-list" @focusout="onDocumentsBlur">
                    <div v-if="person.documents.length === 0" class="documents-empty">
                        {{ $LANG.Get('labels.noDocuments') }}
                    </div>
                    <div v-for="(document, key) in person.documents" :key="key" class="document-row">
                        <i class="fa-solid fa-file document-row-icon"></i>
                        <NevsTextField class="document-row-name" width="260px" v-model="document.name"></NevsTextField>
                        <div class="document-row-file" :title="document.file.name">{{ document.file.name }}</div>
                        <div class="document-row-actions">
                            <span :title="$LANG.Get('tooltips.downloadFile')" class="document-row-button"
                                  @click="downloadDocument(document)"><i class="fa-solid fa-download"></i></span>
                            <span :title="$LANG.Get('tooltips.delete')"
                                  class="document-row-button document-row-button-delete"
                                  @click="deleteDocument(key)"><i class="fa-solid fa-trash"></i></span>
                        </div>
                    </div>
                </div>
            </NevsCard>
        </NevsTab>
        <NevsTab :value="6" v-model="currentTab">
            <NevsCard>
                <div class="invoices-tab-buttons">
                    <NevsButton class="success" @click="openNewInvoice(person.id, {newTab: true})"><i class="fa-solid fa-file-invoice"></i>
                        {{ $LANG.Get('buttons.newInvoice') }}
                    </NevsButton>
                </div>
                <InvoicesGrid v-if="mode === 'edit' && currentTab === 6 && canSeeInvoices" :endpoint="'invoices/by-person/' + person.id"></InvoicesGrid>
            </NevsCard>
        </NevsTab>
        <ModalVisit :show="editVisitModal.show" :person-id="person.id" :visit="editVisitModal.visit"
                    @done="editVisitDone"></ModalVisit>
        <ModalEditPersonMembership :show="editPersonMembershipModal.show" :person-id="person.id"
                                   :person-membership="editPersonMembershipModal.personMembership"
                                   @done="editPersonMembershipDone"></ModalEditPersonMembership>
        <ModalEditPersonProduct :show="editPersonProductModal.show" :person-id="person.id"
                                :person-product="editPersonProductModal.personProduct"
                                @done="editPersonProductDone"></ModalEditPersonProduct>
        <ModalEditPersonPause :show="editPersonPauseModal.show" :person-id="person.id"
                              :pause="editPersonPauseModal.pause"
                              @done="editPersonPauseDone"></ModalEditPersonPause>
    </div>
</template>

<script>
import Person from '@/models/Person.js';
import NevsCard from "@/components/nevs/NevsCard.vue";
import NevsTextField from "@/components/nevs/NevsTextField.vue";
import NevsCheckbox from "@/components/nevs/NevsCheckbox.vue";
import NevsButton from "@/components/nevs/NevsButton.vue";
import NevsTabs from "@/components/nevs/NevsTabs.vue";
import NevsTab from "@/components/nevs/NevsTab.vue";
import NevsTextArea from "@/components/nevs/NevsTextArea.vue";
import NevsSelect from "@/components/nevs/NevsSelect.vue";
import NevsDateField from "@/components/nevs/NevsDateField.vue";
import InvoicesGrid from "@/components/general/InvoicesGrid.vue";
import NevsSimpleGrid from "@/components/nevs/NevsSimpleGrid.vue";
import NevsMultiUpload from "@/components/nevs/NevsMultiUpload.vue";
import NevsPhotoUpload from "@/components/nevs/NevsPhotoUpload.vue";
import ModalVisit from "@/components/modals/ModalVisit.vue";
import ModalEditPersonMembership from "@/components/modals/ModalEditPersonMembership.vue";
import ModalEditPersonProduct from "@/components/modals/ModalEditPersonProduct.vue";
import ModalEditPersonPause from "@/components/modals/ModalEditPersonPause.vue";

export default {
    name: "EntityPerson",
    components: {ModalEditPersonPause, ModalEditPersonProduct, ModalEditPersonMembership, ModalVisit, NevsMultiUpload, NevsPhotoUpload, NevsSimpleGrid, InvoicesGrid, NevsDateField, NevsSelect, NevsTextArea, NevsTabs, NevsTab, NevsButton, NevsCheckbox, NevsTextField, NevsCard},
    data() {
        return {
            loaded: false,
            currentTab: 1,
            mode: this.$route.params.id === '0' ? 'add' : 'edit',
            pristinePerson: null,
            person: new Person({id: this.$route.params.id}),
            personValidation: new Person({}, true),
            visits: [],
            visitsPage: 1,
            visitsTotal: 0,
            memberInitialized: false,
            visitFields: [
                {name: 'checked_in_at_display', label: this.$LANG.Get('fields.checkedInAt')},
                {name: 'checked_out_at_display', label: this.$LANG.Get('fields.checkedOutAt')},
                {name: 'products', label: this.$LANG.Get('fields.products')}
            ],
            personMemberships: [],
            personMembershipFields: [
                {name: 'membership_name', label: this.$LANG.Get('fields.membership')},
                {name: 'start_date_display', label: this.$LANG.Get('fields.startDate')},
                {name: 'end_date_display', label: this.$LANG.Get('fields.endDate')},
                {name: 'entries_display', label: this.$LANG.Get('fields.entries')}
            ],
            editPersonMembershipModal: {
                show: false,
                personMembership: null
            },
            personPauses: [],
            personPauseFields: [
                {name: 'start_date_display', label: this.$LANG.Get('fields.startDate')},
                {name: 'end_date_display', label: this.$LANG.Get('fields.endDate')},
                {name: 'reason', label: this.$LANG.Get('fields.pauseReason')}
            ],
            editPersonPauseModal: {
                show: false,
                pause: null
            },
            personProducts: [],
            personProductFields: [
                {name: 'product_name', label: this.$LANG.Get('fields.product')},
                {name: 'quantity_display', label: this.$LANG.Get('fields.quantity')}
            ],
            editPersonProductModal: {
                show: false,
                personProduct: null
            },
            membershipProducts: [],
            membershipHasActive: false,
            editVisitModal: {
                show: false,
                visit: null
            },
            templates: {
                emails: {
                    address: '',
                    documents: false,
                    official: false,
                    validation: ''
                },
                phones: {
                    number: '',
                    name: '',
                    validation: {
                        number: '',
                        name: ''
                    }
                },
                addresses: {
                    address: '',
                    postal_code: '',
                    city: '',
                    country: '',
                    official: false,
                    validation: {
                        address: '',
                        postal_code: '',
                        city: '',
                        country: ''
                    }
                }
            }
        }
    },
    watch: {
        'person.foreign'() {
            if (this.loaded) {
                this.person.oib_label = 'OIB';
            }
        },
        currentTab() {
            if (this.currentTab === 2) {
                this.loadPersonMemberships();
                this.loadPersonPauses();
            }
            if (this.currentTab === 3) {
                this.loadPersonProducts();
                this.loadMembershipProducts();
            }
            if (this.currentTab === 4) {
                this.reloadVisits();
            }
            // Tab 6 (invoices) reloads via its v-if on currentTab, which remounts InvoicesGrid.
        }
    },
    computed: {
        canSeeInvoices() {
            return this.$HELPERS.HasAnyPermission(this, ['ADMINISTRATOR', 'FINANCES']);
        },
        tabList() {
            return [
                {value: 1, label: this.$LANG.Get('buttons.generalData')},
                {value: 2, label: this.$LANG.Get('buttons.memberships'), visible: this.mode === 'edit'},
                {value: 3, label: this.$LANG.Get('buttons.products'), visible: this.mode === 'edit'},
                {value: 4, label: this.$LANG.Get('buttons.visits'), visible: this.mode === 'edit'},
                {value: 5, label: this.$LANG.Get('buttons.documents'), visible: this.mode === 'edit'},
                {value: 6, label: this.$LANG.Get('buttons.invoices'), visible: this.mode === 'edit' && this.canSeeInvoices}
            ];
        },
        hasUnsavedChanges() {
            if (this.pristinePerson === null) return false;
            // Documents are saved on their own tab (independently of the general "Save" button),
            // so they must not trigger the "unsaved changes" reminder on the first tab.
            let current = JSON.parse(JSON.stringify(this.person));
            let pristine = JSON.parse(this.pristinePerson);
            delete current.documents;
            delete pristine.documents;
            return JSON.stringify(current) !== JSON.stringify(pristine);
        }
    },
    methods: {
        initializeMember() {
            let vm = this;
            this.$LOCAL_BUS.TriggerEvent('popup', {
                type: 'confirm', text: this.$LANG.Get('alerts.confirmInitializeMember'), callback: (response) => {
                    if (!response) return;
                    vm.$API.APICall('post', 'persons/member/initialize/' + vm.person.id, {}, (data, success) => {
                        if (success) {
                            vm.memberInitialized = true;
                            // When the person already had a members-app account (activated through another client)
                            // no password link is sent - they were only notified that this client was added.
                            let message = (data.password_link_sent === false)
                                ? vm.$LANG.Get('alerts.memberActivatedExisting')
                                : vm.$LANG.Get('alerts.memberInitialized');
                            vm.$LOCAL_BUS.TriggerEvent('notification', {text: message});
                        } else if (data && data.error === 'no_official_email') {
                            vm.$LOCAL_BUS.TriggerEvent('popup', {type: 'alert', text: vm.$LANG.Get('alerts.memberNoOfficialEmail')});
                        } else {
                            vm.$HELPERS.HandleServerError(vm, data);
                        }
                    });
                }
            });
        },
        resetMemberPassword() {
            let vm = this;
            this.$LOCAL_BUS.TriggerEvent('popup', {
                type: 'confirm', text: this.$LANG.Get('alerts.confirmResetMemberPassword'), callback: (response) => {
                    if (!response) return;
                    vm.$API.APICall('post', 'persons/member/reset-password/' + vm.person.id, {}, (data, success) => {
                        if (success) {
                            vm.$LOCAL_BUS.TriggerEvent('notification', {text: vm.$LANG.Get('alerts.memberPasswordReset')});
                        } else {
                            vm.$HELPERS.HandleServerError(vm, data);
                        }
                    });
                }
            });
        },
        personMembershipRowClass(record) {
            return record.is_current ? 'person-memberships-grid-current' : null;
        },
        loadPersonMemberships() {
            let vm = this;
            this.$API.APICall('get', 'person-memberships/by-person/' + this.person.id, {}, (data, success) => {
                if (success) {
                    vm.personMemberships = data.records;
                } else {
                    vm.$HELPERS.HandleServerError(vm, data);
                }
            });
        },
        addPersonMembership() {
            this.editPersonMembershipModal.personMembership = null;
            this.editPersonMembershipModal.show = true;
        },
        editPersonMembership(personMembership) {
            this.editPersonMembershipModal.personMembership = personMembership;
            this.editPersonMembershipModal.show = true;
        },
        editPersonMembershipDone(success) {
            this.editPersonMembershipModal.show = false;
            this.editPersonMembershipModal.personMembership = null;
            if (success) {
                this.loadPersonMemberships();
            }
        },
        deletePersonMembership(personMembership) {
            let vm = this;
            this.$LOCAL_BUS.TriggerEvent('popup', {
                type: 'confirm', text: this.$LANG.Get('alerts.generalRemovalQuestion'), callback: (response) => {
                    if (response) {
                        vm.$API.APICall('delete', 'person-memberships/' + personMembership.id, {}, (data, success) => {
                            if (success) {
                                vm.loadPersonMemberships();
                            } else {
                                vm.$HELPERS.HandleServerError(vm, data);
                            }
                        });
                    }
                }
            });
        },
        personPauseRowClass(record) {
            return record.is_current ? 'person-pauses-grid-current' : null;
        },
        loadPersonPauses() {
            let vm = this;
            this.$API.APICall('get', 'person-pauses/by-person/' + this.person.id, {}, (data, success) => {
                if (success) {
                    vm.personPauses = data.records;
                } else {
                    vm.$HELPERS.HandleServerError(vm, data);
                }
            });
        },
        addPersonPause() {
            this.editPersonPauseModal.pause = null;
            this.editPersonPauseModal.show = true;
        },
        editPersonPause(pause) {
            this.editPersonPauseModal.pause = pause;
            this.editPersonPauseModal.show = true;
        },
        editPersonPauseDone(success) {
            this.editPersonPauseModal.show = false;
            this.editPersonPauseModal.pause = null;
            if (success) {
                this.loadPersonPauses();
                // A pause changes the end date of every membership this person holds, so the list above it is
                // re-read rather than left showing the dates from before the change.
                this.loadPersonMemberships();
            }
        },
        deletePersonPause(pause) {
            let vm = this;
            this.$LOCAL_BUS.TriggerEvent('popup', {
                type: 'confirm', text: this.$LANG.Get('alerts.generalRemovalQuestion'), callback: (response) => {
                    if (response) {
                        vm.$API.APICall('delete', 'person-pauses/' + pause.id, {}, (data, success) => {
                            if (success) {
                                vm.loadPersonPauses();
                                vm.loadPersonMemberships();
                            } else {
                                vm.$HELPERS.HandleServerError(vm, data);
                            }
                        });
                    }
                }
            });
        },
        loadPersonProducts() {
            let vm = this;
            this.$API.APICall('get', 'person-products/by-person/' + this.person.id, {}, (data, success) => {
                if (success) {
                    vm.personProducts = data.records;
                } else {
                    vm.$HELPERS.HandleServerError(vm, data);
                }
            });
        },
        addPersonProduct() {
            this.editPersonProductModal.personProduct = null;
            this.editPersonProductModal.show = true;
        },
        editPersonProduct(personProduct) {
            this.editPersonProductModal.personProduct = personProduct;
            this.editPersonProductModal.show = true;
        },
        editPersonProductDone(success) {
            this.editPersonProductModal.show = false;
            this.editPersonProductModal.personProduct = null;
            if (success) {
                this.loadPersonProducts();
            }
        },
        deletePersonProduct(personProduct) {
            let vm = this;
            this.$LOCAL_BUS.TriggerEvent('popup', {
                type: 'confirm', text: this.$LANG.Get('alerts.generalRemovalQuestion'), callback: (response) => {
                    if (response) {
                        vm.$API.APICall('delete', 'person-products/' + personProduct.id, {}, (data, success) => {
                            if (success) {
                                vm.loadPersonProducts();
                            } else {
                                vm.$HELPERS.HandleServerError(vm, data);
                            }
                        });
                    }
                }
            });
        },
        loadMembershipProducts() {
            let vm = this;
            this.$API.APICall('get', 'person-membership-products/by-person/' + this.person.id, {}, (data, success) => {
                if (success) {
                    vm.membershipHasActive = data.has_active_membership;
                    vm.membershipProducts = data.records;
                } else {
                    vm.$HELPERS.HandleServerError(vm, data);
                }
            });
        },
        showMembershipProducts(personMembership) {
            let lines = personMembership.products.map((product) => product.product_name + ' (' + product.quantity_display + ')');
            this.$LOCAL_BUS.TriggerEvent('popup', {
                type: 'alert',
                text: lines.join('<br>')
            });
        },
        loadVisits() {
            let vm = this;
            this.$API.APICall('get', 'visits/by-person/' + this.person.id, {page: this.visitsPage}, (data, success) => {
                if (success) {
                    vm.visits = vm.visits.concat(data.records);
                    vm.visitsTotal = data.total_records;
                } else {
                    vm.$HELPERS.HandleServerError(vm, data);
                }
            });
        },
        loadMoreVisits() {
            this.visitsPage++;
            this.loadVisits();
        },
        reloadVisits() {
            this.visits = [];
            this.visitsPage = 1;
            this.loadVisits();
        },
        addVisit() {
            this.editVisitModal.visit = null;
            this.editVisitModal.show = true;
        },
        editVisit(visit) {
            this.editVisitModal.visit = visit;
            this.editVisitModal.show = true;
        },
        editVisitDone(success) {
            this.editVisitModal.show = false;
            this.editVisitModal.visit = null;
            if (success) {
                this.reloadVisits();
            }
        },
        deleteVisit(visit) {
            let vm = this;
            this.$LOCAL_BUS.TriggerEvent('popup', {
                type: 'confirm', text: this.$LANG.Get('alerts.generalRemovalQuestion'), callback: (response) => {
                    if (response) {
                        vm.$API.APICall('delete', 'visits/' + visit.id, {}, (data, success) => {
                            if (success) {
                                vm.reloadVisits();
                            } else {
                                vm.$HELPERS.HandleServerError(vm, data);
                            }
                        });
                    }
                }
            });
        },
        /**
         * On a new person, pre-fill the member ID with the next free one when the client has automatic member
         * IDs turned on. Only a suggestion: the field stays editable, and the number is not reserved, so the
         * one that is actually saved is whatever is in the field on save. Silently does nothing when the
         * setting is off, the call fails, or the field has already been typed into by the time it answers.
         */
        prefillMemberId() {
            let vm = this;
            this.$API.APICall('get', 'persons/next-member-id', {}, (data, success) => {
                if (success && data.enabled && vm.person.member_id === '') {
                    vm.person.member_id = data.member_id;
                }
            });
        },
        documentsUploaded(files) {
            for (let file of files) {
                this.person.documents.push({
                    name: file.name,
                    file: file
                });
            }
            this.saveDocuments();
        },
        downloadDocument(document) {
            window.open(document.file.link);
        },
        deleteDocument(key) {
            let vm = this;
            this.$LOCAL_BUS.TriggerEvent('popup', {
                type: 'confirm', text: this.$LANG.Get('alerts.fileDeletionQuestion'), callback: (response) => {
                    if (response) {
                        vm.person.documents.splice(key, 1);
                        vm.saveDocuments();
                    }
                }
            });
        },
        documentsChanged() {
            if (this.pristinePerson === null) return true;
            let saved = JSON.parse(this.pristinePerson).documents || [];
            return JSON.stringify(saved) !== JSON.stringify(this.person.documents);
        },
        onDocumentsBlur() {
            if (this.documentsChanged()) {
                this.saveDocuments(false);
            }
        },
        saveDocuments(notify = true) {
            let vm = this;
            this.$API.APICall('put', 'persons/documents/' + this.person.id, {documents: this.person.documents}, (data, success) => {
                if (success) {
                    let pristine = JSON.parse(vm.pristinePerson);
                    pristine.documents = JSON.parse(JSON.stringify(vm.person.documents));
                    vm.pristinePerson = JSON.stringify(pristine);
                    if (notify) {
                        vm.$LOCAL_BUS.TriggerEvent('notification', {text: vm.$LANG.Get('alerts.recordSaved')});
                    }
                } else {
                    vm.$HELPERS.HandleServerError(vm, data);
                }
            });
        },
        removeRecord(key, type) {
            let vm = this;
            this.$LOCAL_BUS.TriggerEvent('popup', {
                type: 'confirm', text: this.$LANG.Get('alerts.generalRemovalQuestion'), callback: (response) => {
                    if (response) {
                        vm.person[type].splice(key, 1);
                    }
                }
            });
        },
        addRecord(type) {
            this.person[type].push(JSON.parse(JSON.stringify(this.templates[type])))
        },
        backClick() {
            if (window.history.length === 1) {
                window.close();
            } else {
                this.$router.back();
            }
        },
        loadPerson(done) {
            let vm = this;
            this.$API.APICall('get', 'persons/' + this.person.id, {}, (data, success) => {
                if (success) {
                    if (data.person !== null) {
                        vm.person.Fill(data.person);
                        // The server stores no photo as null; the upload control needs an (empty) object.
                        if (!vm.person.photo || !vm.person.photo.id) {
                            vm.person.photo = {name: '', link: '', id: 0};
                        }
                        vm.memberInitialized = data.person.member_initialized === true;
                        vm.pristinePerson = JSON.stringify(vm.person);
                        vm.$store.commit('setBreadcrumbs', [
                            {
                                label: vm.$LANG.Get('modules.clients'),
                                link: null
                            },
                            {
                                label: vm.$LANG.Get('modules.persons'),
                                link: '/persons'
                            },
                            {
                                label: vm.person.first_name + ' ' + vm.person.last_name,
                                link: null
                            }
                        ]);
                    } else {
                        vm.$HELPERS.HandleServerError(vm, data);
                    }
                } else {
                    vm.$HELPERS.HandleServerError(vm, data);
                }
                if (done) done();
            });
        },
        // Opens the new-invoice screen for a person. The invoice is issued to a customer, not to a person, so
        // the person's customer (created together with the person) is resolved first and handed to
        // EntityInvoice through the URL.
        //   newTab           opens the invoice in a new browser tab and leaves this screen where it is. The
        //                    "Računi" tab's grid picks the new invoice up on its own through the
        //                    reload-invoices cross-tab event.
        //   returnOnFailure  goes back to the list when the person has no customer. Set when this follows the
        //                    save of a new person, which is saved either way.
        openNewInvoice(personId, {newTab = false, returnOnFailure = false} = {}) {
            let vm = this;
            this.$API.APICall('get', 'persons/customer/' + personId, {}, (data, success) => {
                if (success && data.customer_id !== null) {
                    let path = '/invoices/0/add?customer_id=' + data.customer_id;
                    if (newTab) {
                        window.open(vm.$router.resolve(path).href);
                    } else {
                        vm.$router.push(path);
                    }
                } else {
                    vm.$LOCAL_BUS.TriggerEvent('popup', {
                        text: vm.$LANG.Get('alerts.personHasNoCustomer'),
                        type: 'alert'
                    });
                    if (returnOnFailure) vm.backClick();
                }
            });
        },
        // `createInvoice` is set by the "Save and create new invoice" button: on a successful save of a new
        // person it opens the new-invoice screen with that person's customer already selected.
        saveClick(createInvoice = false) {
            if (this.person.addresses.length === 0) {
                this.person.addresses.push(this.templates.addresses);
            }
            if (this.person.addresses.length === 1) {
                this.person.addresses[0].official = true;
            }
            if (this.person.emails.length === 1) {
                this.person.emails[0].official = true;
            }

            let valid = true;

            if (this.person.first_name === '') {
                valid = false;
                this.personValidation.first_name = this.$LANG.Get('labels.mandatoryField');
            } else {
                this.personValidation.first_name = '';
            }

            if (this.person.last_name === '') {
                valid = false;
                this.personValidation.last_name = this.$LANG.Get('labels.mandatoryField');
            } else {
                this.personValidation.last_name = '';
            }

            if (this.person.oib !== '') {
                if (!this.person.foreign) {
                    if (!this.$HELPERS.CheckOIB(this.person.oib)) {
                        valid = false;
                        this.personValidation.oib = this.$LANG.Get('labels.invalidOIB');
                    } else {
                        this.personValidation.oib = '';
                    }
                } else {
                    this.personValidation.oib = '';
                }
            }

            if (this.person.emails.length !== 0) {
                for (let email of this.person.emails) {
                    if (email.address === '') {
                        valid = false;
                        email.validation = this.$LANG.Get('labels.mandatoryField');
                    } else {
                        if (!this.$HELPERS.CheckEmail(email.address)) {
                            valid = false;
                            email.validation = this.$LANG.Get('labels.invalidEmailAddress');
                        } else {
                            email.validation = '';
                        }
                    }
                }
                let officialEmails = 0;
                for (let email of this.person.emails) {
                    if (email.official) {
                        officialEmails++;
                    }
                }
                if (officialEmails !== 1) {
                    valid = false;
                    this.personValidation.emails = this.$LANG.Get('labels.defaultEmailFail');
                } else {
                    this.personValidation.emails = '';
                }
            } else {
                this.personValidation.emails = '';
            }

            let official = 0;
            for (let address of this.person.addresses) {
                if (address.official) {
                    official++;
                }
            }
            if (official !== 1) {
                valid = false;
                this.personValidation.addresses = this.$LANG.Get('labels.defaultAddressFail');
            } else {
                this.personValidation.addresses = '';
            }

            for (let phone of this.person.phones) {
                if (phone.name === '') {
                    valid = false;
                    phone.validation.name = this.$LANG.Get('labels.mandatoryField');
                } else {
                    phone.validation.name = '';
                }
                if (phone.number === '') {
                    valid = false;
                    phone.validation.number = this.$LANG.Get('labels.mandatoryField');
                } else {
                    phone.validation.number = '';
                }
            }

            if (valid) {
                let vm = this;
                let requestMethod = this.mode === 'add' ? 'post' : 'put';
                let requestService = this.mode === 'add' ? 'persons' : 'persons/' + this.person.id;
                this.$API.APICall(requestMethod, requestService, this.person, (data, success) => {
                    if (success) {
                        vm.pristinePerson = JSON.stringify(vm.person);
                        // Changing the official e-mail de-initializes the person for the members app, so the
                        // buttons in the members-app panel have to follow the state the save left behind.
                        if (data.member_initialized !== undefined) {
                            vm.memberInitialized = data.member_initialized === true;
                        }
                        vm.$CROSS_TAB_BUS.TriggerEvent('reload-persons', {});
                        vm.$LOCAL_BUS.TriggerEvent('notification', {text: this.$LANG.Get('alerts.recordSaved')});
                        if (vm.mode === 'add') {
                            if (createInvoice) {
                                vm.openNewInvoice(data.person.id, {returnOnFailure: true});
                            } else {
                                vm.backClick();
                            }
                        }
                    } else {
                        if (data.error === 'oib_duplicate') {
                            vm.$LOCAL_BUS.TriggerEvent('popup', {
                                text: vm.$LANG.Get('alerts.oibDuplicate'),
                                type: 'alert'
                            });
                        } else if (data.error === 'email_duplicate') {
                            vm.$LOCAL_BUS.TriggerEvent('popup', {
                                text: vm.$LANG.Get('alerts.emailDuplicate'),
                                type: 'alert'
                            });
                        } else {
                            vm.$HELPERS.HandleServerError(vm, data);
                        }
                    }
                })
            }
        }
    },
    mounted() {

        this.$store.commit('selectMenu', 'clients');
        this.$store.commit('selectSubMenu', 'persons');

        if (this.mode === 'add') {
            this.$store.commit('setBreadcrumbs', [
                {
                    label: this.$LANG.Get('modules.clients'),
                    link: null
                },
                {
                    label: this.$LANG.Get('modules.persons'),
                    link: '/persons'
                },
                {
                    label: this.$LANG.Get('modules.newPerson'),
                    link: null
                }
            ]);
            this.loaded = true;
            this.prefillMemberId();
        } else {
            let vm = this;
            this.loadPerson(() => {
                vm.$nextTick(() => {
                    vm.loaded = true;
                });
            });
        }
    }
}
</script>

<style scoped lang="scss">
@use '../../scss/vars' as *;

.invoices-tab-buttons {
    margin-bottom: 12px;
}

.unsaved-changes {
    margin-left: 10px;
    color: $danger;
    font-size: 13px;
}

.person-tabs {
    margin-top: 18px;
    margin-bottom: 6px;
}

.form {
    max-width: 300px;
    margin-bottom: 10px;
    padding: 15px;
}

.form-float {
    width: 300px;
    float: left;
    margin-right: 10px;
    margin-bottom: 10px;
    padding: 15px;
}

.form-float h2 {
    margin: 0 0 5px;
}

.person-form {
    max-width: 1080px;
    padding: 16px;
}

.person-form-header {
    display: flex;
    align-items: center;
    gap: 24px;
    flex-wrap: wrap;
    margin-bottom: 6px;
    padding-bottom: 14px;
    border-bottom: 1px solid $sand-300;
}

.person-form-header-photo {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
}

.person-form-header-fields {
    flex: 1 1 240px;
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 0 12px;
}

.person-form-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
    gap: 4px 12px;
    align-items: start;
    margin-top: 8px;
}

.person-form-foreign {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
    padding: 8px 5px 0;
}

.person-form-flags {
    display: flex;
    flex-wrap: wrap;
    gap: 10px 28px;
    padding: 10px 5px;
}

.person-form-note {
    padding: 0 5px;
}

.person-form-sections {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 22px;
    margin-top: 18px;
    padding-top: 16px;
    border-top: 1px solid $sand-300;
}

.person-form-section h2 {
    margin: 0 0 8px;
    font-size: 1.05em;
}

.box-form {
    border: 2px solid $sand-400;
    padding: 5px;
    border-radius: 5px;
    margin-bottom: 5px;
    position: relative;
}

.box-error {
    font-size: 12px;
    margin-bottom: 5px;
    text-align: justify;
}

.box-form-remove-button {
    cursor: pointer;
    position: absolute;
    right: 5px;
    bottom: 5px;
}

.visit-product-quantity {
    color: $ink-muted;
}

.visits-grid-button {
    cursor: pointer;
}

.visits-grid-button-delete {
    margin-left: 10px;
}

.person-memberships-grid {
    margin-top: 15px;
}

.person-memberships-grid-button {
    cursor: pointer;
}

.person-memberships-grid-button-delete {
    margin-left: 10px;
}

:deep(.person-memberships-grid-current td) {
    background-color: $success-surface;
}

.person-pauses-heading {
    margin-top: 30px;
}

.person-pauses-disclaimer {
    margin-bottom: 12px;
    color: $ink-muted;
    font-size: 0.9em;
}

.person-pauses-grid {
    margin-top: 15px;
}

.person-pauses-grid-button {
    cursor: pointer;
}

.person-pauses-grid-button-delete {
    margin-left: 10px;
}

/* A pause running right now, i.e. the person is frozen today. */
:deep(.person-pauses-grid-current td) {
    background-color: $warning-surface;
}

.person-products-heading {
    margin: 0 0 10px;
}

.person-membership-products-card {
    margin-top: 10px;
}

.person-membership-products-none {
    color: $ink-muted;
    font-style: italic;
}

.person-memberships-grid-button-products {
    margin-right: 10px;
}

.person-products-grid {
    margin-top: 15px;
}

.person-products-grid-button {
    cursor: pointer;
}

.person-products-grid-button-delete {
    margin-left: 10px;
}

.visits-grid {
    margin-top: 10px;
}

.visits-load-more {
    margin-top: 10px;
}

.documents-card {
    max-width: 600px;
}

.documents-list {
    margin-top: 20px;
}

.documents-empty {
    color: $ink-muted;
    font-style: italic;
}

.document-row {
    display: flex;
    align-items: center;
    gap: 12px;
    border: 2px solid $sand-400;
    border-radius: 5px;
    padding: 8px 12px;
    margin-bottom: 8px;
    background: $white;
}

.document-row-icon {
    font-size: 22px;
    color: $orange-icon;
    flex-shrink: 0;
}

.document-row-name {
    width: 260px;
    flex-shrink: 0;
}

.document-row-file {
    flex: 1;
    min-width: 0;
    color: $ink-muted;
    font-size: 13px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.document-row-actions {
    display: flex;
    gap: 12px;
    flex-shrink: 0;
}

.document-row-button {
    cursor: pointer;
    color: $ink-muted;
}

.document-row-button:hover {
    color: $ink-strong;
}

.document-row-button-delete:hover {
    color: $danger;
}

.members-app-status {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 0 0 12px;
    color: $ink-muted;
}

.members-app-status.initialized {
    color: $success;
}
</style>